/**
 * naoConformidades.js — Onda 109
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * O texto de cada não conformidade que uma carga da API de Integração pode ter.
 *
 * Por que os textos moram aqui, e não no servidor: quem lê este relatório é o
 * gestor do clube ou do condomínio, e ele precisa de três coisas em ordem — o
 * que aconteceu, o que isso causou no cadastro dele, e o que pedir ao
 * desenvolvedor do sistema de gestão. O servidor classifica (`kind`); a tela
 * explica.
 *
 * Os textos saíram do caso real de 22/09/2026 no Ipê Clube: 590 itens recusados
 * em 7.870, todos por placa compartilhada entre sócios, e 295 associados que não
 * entraram porque o lote reverte o associado inteiro quando um veículo falha.
 */

const CAUSAS = {
  placa_ja_ativa: {
    titulo: 'Placa já ativa em outro veículo',
    gravidade: 'alta',
    oQueE: 'O mesmo carro chegou vinculado a dois cadastros diferentes. A plataforma mantém uma placa ativa em um único veículo, porque a leitura da câmera precisa saber a quem atribuir o acesso.',
    efeito: 'O item inteiro é revertido — não só o veículo. O associado (ou a unidade) não entra no cadastro, com nome, situação e os outros veículos dele.',
    correcao: 'Use um identificador de veículo estável por placa, em vez de um por cadastro. O mesmo identificador chegando com outro titular faz a plataforma transferir o veículo, sem erro. Se o identificador precisar continuar por cadastro, envie o veículo do titular anterior como inativo antes de ativá-lo no novo.',
  },
  placa_invalida: {
    titulo: 'Placa fora do padrão',
    gravidade: 'alta',
    oQueE: 'A placa enviada não tem sete ou oito caracteres, ou traz símbolo que não existe numa placa.',
    efeito: 'O item é recusado por inteiro, como acima.',
    correcao: 'Normalize a placa antes de enviar: só letras e dígitos, sem hífen, ponto ou espaço.',
  },
  campo_obrigatorio: {
    titulo: 'Campo obrigatório ausente ou longo demais',
    gravidade: 'media',
    oQueE: 'Faltou um campo que a plataforma exige, ou ele veio maior do que o limite.',
    efeito: 'O item é recusado; os demais do mesmo lote seguem normalmente.',
    correcao: 'Confira o contrato em Integração → Documentação da Integração: cada campo traz o tamanho máximo.',
  },
  vinculo_inexistente: {
    titulo: 'Vínculo apontando para algo que não existe',
    gravidade: 'media',
    oQueE: 'O item cita um cadastro, uma unidade ou uma execução de carga que a plataforma não conhece.',
    efeito: 'O item é recusado.',
    correcao: 'Envie o cadastro-pai antes do dependente, e abra a execução de carga antes de enviar os lotes que a citam.',
  },
  outro: {
    titulo: 'Outras recusas',
    gravidade: 'media',
    oQueE: 'Recusas que ainda não têm uma explicação própria nesta tela.',
    efeito: 'O item não entrou no cadastro.',
    correcao: 'A mensagem completa está na lista abaixo — é o mesmo texto que o integrador recebeu na resposta.',
  },
};

/** A explicação de uma causa, com um padrão seguro para causa desconhecida. */
export function explicacao(kind) {
  return CAUSAS[kind] || { ...CAUSAS.outro, titulo: kind };
}

/**
 * As observações sobre o JEITO como a carga foi feita — não são itens
 * recusados, e por isso não aparecem na contagem de não conformidades. Cada uma
 * só entra no relatório quando o servidor a marcou.
 */
export const OBSERVACOES = {
  sem_execucao_declarada: {
    titulo: 'Carga sem execução declarada',
    gravidade: 'media',
    texto: 'Esta carga não abriu uma execução de sincronização. Sem ela, a plataforma recebe quem veio, mas não tem como saber quem deixou de vir: o cadastro desligado do outro lado continua ativo aqui, com o veículo liberado.',
    correcao: 'Para a carga periódica, abra uma execução no modo completo, envie os lotes citando-a e feche ao final — o que não apareceu é inativado automaticamente.',
  },
  bloqueios_por_limite: {
    titulo: 'Bloqueios por limite de requisições',
    gravidade: 'alta',
    texto: 'A chave passou do número de requisições por minuto e a plataforma recusou as excedentes.',
    correcao: 'Respeite o tempo devolvido no cabeçalho Retry-After e repita — a repetição é segura quando a requisição leva chave de idempotência. Lotes maiores também resolvem: o limite é por requisição, não por item.',
  },
  escopo_negado: {
    titulo: 'Escopo negado',
    gravidade: 'alta',
    texto: 'A chave tentou um endereço que pertence a outra vertical, ou a um escopo que ela não tem.',
    correcao: 'Confira os escopos da chave em Integração → Chaves de API: cada instalação só oferece os da sua vertical.',
  },
  lote_invalido: {
    titulo: 'Lote recusado por inteiro',
    gravidade: 'alta',
    texto: 'Alguma requisição veio com o lote fora do tamanho aceito (de 1 a 500 itens) ou mal formada.',
    correcao: 'Divida a carga em lotes de até 500 itens.',
  },
  erro_servidor: {
    titulo: 'Erro no servidor durante a carga',
    gravidade: 'alta',
    texto: 'Uma ou mais requisições terminaram em erro do lado da plataforma.',
    correcao: 'Vale investigar no log do servidor; os itens dessas requisições não entraram e precisam ser reenviados.',
  },
  lote_pequeno: {
    titulo: 'Lotes pequenos',
    gravidade: 'baixa',
    texto: 'Os lotes trouxeram poucos itens cada, o que multiplica o número de requisições e aproxima a carga do limite por minuto sem necessidade.',
    correcao: 'O limite é de 500 itens por lote.',
  },
  perto_do_limite: {
    titulo: 'Ritmo perto do limite',
    gravidade: 'baixa',
    texto: 'Em algum minuto a carga chegou perto do limite de requisições da chave. Não houve bloqueio, mas faltou pouco.',
    correcao: 'Aumentar o tamanho do lote é o caminho mais simples.',
  },
};

/** Só as observações que o servidor marcou, na ordem em que devem ser lidas. */
export function observacoesAtivas(observacoes = {}) {
  const ordem = ['bloqueios_por_limite', 'escopo_negado', 'lote_invalido', 'erro_servidor',
    'sem_execucao_declarada', 'perto_do_limite', 'lote_pequeno'];
  return ordem
    .filter(chave => {
      const valor = observacoes[chave];
      return typeof valor === 'boolean' ? valor : Number(valor) > 0;
    })
    .map(chave => ({
      chave,
      quantidade: typeof observacoes[chave] === 'number' ? observacoes[chave] : null,
      ...OBSERVACOES[chave],
    }));
}

/** Um veredito de uma linha para a carga inteira, para o topo do relatório. */
export function veredito({ items_total = 0, items_failed = 0 } = {}, observacoes = []) {
  if (!items_total) return { tom: 'neutro', texto: 'Esta carga não trouxe itens.' };
  if (!items_failed && !observacoes.length) {
    return { tom: 'bom', texto: 'Carga sem não conformidades: todos os itens entraram.' };
  }
  const pct = ((items_failed / items_total) * 100).toFixed(1);
  if (!items_failed) {
    return { tom: 'atencao', texto: 'Todos os itens entraram, mas há observações sobre como a carga foi feita.' };
  }
  return {
    tom: items_failed / items_total >= 0.02 ? 'ruim' : 'atencao',
    texto: `${items_failed} de ${items_total} itens foram recusados (${pct}%) e não estão no cadastro.`,
  };
}

export default CAUSAS;
